import { clamp } from "../utils.js";

export function animationProgress(animation, poseName, progress = null) {
  if (progress !== null && progress !== undefined) {
    return clamp(Number(progress) || 0, 0, 1);
  }
  if (poseName === "idle") {
    return (performance.now() % 1600) / 1600;
  }
  return 0;
}

export function sampleKeyValue(frames, key, t, fallback = 0) {
  const keyedFrames = (frames || []).filter((frame) => frame[key] !== undefined);
  if (!keyedFrames.length) {
    return fallback;
  }
  let previous = keyedFrames[0];
  let next = keyedFrames[keyedFrames.length - 1];
  for (const frame of keyedFrames) {
    if (frame.t <= t) previous = frame;
    if (frame.t >= t) {
      next = frame;
      break;
    }
  }
  if (previous === next || previous.t === next.t) {
    return Number(previous[key] ?? fallback);
  }
  const amount = clamp((t - previous.t) / (next.t - previous.t), 0, 1);
  return Number(previous[key] ?? fallback) + (Number(next[key] ?? fallback) - Number(previous[key] ?? fallback)) * amount;
}

export function sampleBoneOffset(animation, boneName, t) {
  const frames = animation?.bones?.[boneName] || [];
  return {
    x: sampleKeyValue(frames, "x", t, 0),
    y: sampleKeyValue(frames, "y", t, 0),
    rotation: sampleKeyValue(frames, "rotation", t, 0),
    scale: sampleKeyValue(frames, "scale", t, 1),
  };
}

export function sampleSkeletonPose(skeletonData, animationName, progress = null) {
  const animation = skeletonData.animations[animationName] || skeletonData.animations.idle;
  const t = animationProgress(animation, animationName, progress);
  const pose = {};
  Object.keys(skeletonData.bones).forEach((boneName) => {
    const bone = skeletonData.bones[boneName];
    const offset = sampleBoneOffset(animation, boneName, t);
    pose[boneName] = {
      x: Number(bone.x || 0) + offset.x,
      y: Number(bone.y || 0) + offset.y,
      rotation: Number(bone.rotation || 0) + offset.rotation,
      scale: Number(bone.scale || 1) * offset.scale,
    };
  });
  return pose;
}

export function blendSkeletonPoses(skeletonData, fromPose, toPose, amount) {
  const alpha = clamp(Number(amount) || 0, 0, 1);
  const pose = {};
  Object.keys(skeletonData.bones).forEach((boneName) => {
    const from = fromPose?.[boneName] || {};
    const to = toPose?.[boneName] || {};
    pose[boneName] = {
      x: Number(from.x ?? to.x ?? 0) + (Number(to.x ?? from.x ?? 0) - Number(from.x ?? to.x ?? 0)) * alpha,
      y: Number(from.y ?? to.y ?? 0) + (Number(to.y ?? from.y ?? 0) - Number(from.y ?? to.y ?? 0)) * alpha,
      rotation: Number(from.rotation ?? to.rotation ?? 0) + (Number(to.rotation ?? from.rotation ?? 0) - Number(from.rotation ?? to.rotation ?? 0)) * alpha,
      scale: Number(from.scale ?? to.scale ?? 1) + (Number(to.scale ?? from.scale ?? 1) - Number(from.scale ?? to.scale ?? 1)) * alpha,
    };
  });
  return pose;
}

export function skeletonWorldTransforms(skeletonData, pose) {
  const transforms = {};
  const resolve = (boneName) => {
    if (transforms[boneName]) return transforms[boneName];
    const bone = skeletonData.bones[boneName];
    if (!bone) return { x: 0, y: 0, rotation: 0, scale: 1 };
    const local = pose?.[boneName] || {
      x: Number(bone.x || 0),
      y: Number(bone.y || 0),
      rotation: Number(bone.rotation || 0),
      scale: Number(bone.scale || 1),
    };
    if (!bone.parent) {
      transforms[boneName] = local;
      return local;
    }
    const parent = resolve(bone.parent);
    const cos = Math.cos(parent.rotation);
    const sin = Math.sin(parent.rotation);
    transforms[boneName] = {
      x: parent.x + local.x * parent.scale * cos - local.y * parent.scale * sin,
      y: parent.y + local.x * parent.scale * sin + local.y * parent.scale * cos,
      rotation: parent.rotation + local.rotation,
      scale: parent.scale * local.scale,
    };
    return transforms[boneName];
  };
  Object.keys(skeletonData.bones).forEach(resolve);
  return transforms;
}
